<script setup lang="ts">
import { Package } from "@lucide/vue";
import { computed, ref } from "vue";
import draggable from "vuedraggable";
import CustomBlockIcon from "./CustomBlockIcon.vue";
import { blockTypeIcons } from "../utils/blockTypeIcons";
import {
  useBlockPalette,
  type BlockPaletteItem,
} from "../composables/useBlockPalette";
import { useCloudI18n } from "../composables/useCloudI18n";

/** `standalone`: docked to viewport left. `popupAdjacent`: beside popup icon rail. */
const props = withDefaults(
  defineProps<{ layout?: "standalone" | "popupAdjacent" }>(),
  { layout: "standalone" },
);

const hoverExpanded = ref(false);

/** Popup chrome uses a narrow strip; keep labels visible without hover. Standalone email sidebar stays hover/focus-expand. */
const isExpanded = computed(
  () => props.layout === "popupAdjacent" || hoverExpanded.value,
);

function setAsideExpanded(next: boolean): void {
  if (props.layout === "popupAdjacent") return;
  hoverExpanded.value = next;
}

const {
  t,
  format,
  blockTypes,
  createBlockFromItem,
  insertBlockFromItem,
  handlePaletteKeydown,
  paletteRowBlocked,
  paletteBlockedReason,
  showModulesSection,
  caps,
} = useBlockPalette();

const { t: cloudT } = useCloudI18n();

/** Fixed tooltip when a singleton palette row is disabled (discount / form). */
const paletteBlockedTooltipPos = ref<{
  left: number;
  top: number;
  message: string;
} | null>(null);

function onPaletteRowHoverEnter(
  blockType: BlockPaletteItem,
  ev: MouseEvent | FocusEvent,
): void {
  const msg = paletteBlockedReason(blockType);
  if (!msg) {
    paletteBlockedTooltipPos.value = null;
    return;
  }
  const row =
    ("currentTarget" in ev &&
      ev.currentTarget &&
      (ev.currentTarget as HTMLElement)) ??
    null;
  if (!row) return;
  const btn = row.querySelector("button");
  const el = btn ?? row;
  const rect = el.getBoundingClientRect();
  paletteBlockedTooltipPos.value = {
    left: rect.left + rect.width / 2,
    top: rect.top,
    message: msg,
  };
}

function onPaletteRowHoverLeave(): void {
  paletteBlockedTooltipPos.value = null;
}

function openModulesBrowser(): void {
  caps.savedModules?.openBrowser();
}
</script>

<template>
  <aside
    :aria-label="t.sidebarNav.palette"
    :class="
      props.layout === 'standalone'
        ? 'tpl-sidebar-rail tpl:absolute tpl:top-14 tpl:bottom-0 tpl:left-0 tpl:z-40 tpl:overflow-hidden'
        : 'tpl-sidebar-rail tpl:relative tpl:z-0 tpl:h-full tpl:min-h-0 tpl:shrink-0 tpl:overflow-hidden'
    "
    :style="{
      width: isExpanded ? '200px' : '48px',
      backgroundColor: 'var(--tpl-bg-elevated)',
      borderRight: '1px solid var(--tpl-border)',
      boxShadow: isExpanded ? 'var(--tpl-shadow-lg)' : 'none',
      transition:
        'width 200ms cubic-bezier(0.16, 1, 0.3, 1), box-shadow 200ms cubic-bezier(0.16, 1, 0.3, 1)',
    }"
    @mouseenter="setAsideExpanded(true)"
    @mouseleave="setAsideExpanded(false)"
    @focusin="setAsideExpanded(true)"
    @focusout="setAsideExpanded(false)"
  >
    <!-- Saved Modules browser trigger (cloud only) -->
    <div
      v-if="showModulesSection && cloudT"
      class="tpl:border-b tpl:px-1 tpl:pb-1 tpl:border-[var(--tpl-border)]"
    >
      <button
        type="button"
        :aria-label="t.sidebarNav.browseModules"
        class="tpl:flex tpl:h-10 tpl:w-full tpl:cursor-pointer tpl:items-center tpl:gap-3 tpl:rounded-[var(--tpl-radius-sm)] tpl:border-none tpl:bg-transparent tpl:px-3 tpl:text-[var(--tpl-text-muted)] tpl:transition-all tpl:duration-[120ms] hover:tpl:bg-[var(--tpl-primary-light)] hover:tpl:text-[var(--tpl-primary)]"
        :style="{
          justifyContent: isExpanded ? 'flex-start' : 'center',
        }"
        @click="openModulesBrowser"
      >
        <Package :size="20" :stroke-width="1.5" class="tpl:shrink-0" />
        <span
          v-if="isExpanded"
          class="tpl:flex-1 tpl:truncate tpl:text-sm tpl:font-medium"
        >
          {{ cloudT.modules.title }}
        </span>
        <span
          v-if="isExpanded"
          class="tpl:shrink-0 tpl:rounded-full tpl:px-1.5 tpl:py-0.5 tpl:text-[10px] tpl:font-medium tpl:bg-[var(--tpl-bg-hover)] tpl:text-[var(--tpl-text-muted)]"
        >
          {{ caps.savedModules?.moduleCount.value ?? 0 }}
        </span>
      </button>
    </div>
    <draggable
      filter=".tpl-sidebar-palette-item--blocked"
      :list="blockTypes"
      :group="{ name: 'blocks', pull: 'clone', put: false }"
      :clone="createBlockFromItem"
      :sort="false"
      item-key="type"
      :animation="150"
      ghost-class="tpl-ghost"
      class="tpl:flex tpl:flex-col tpl:gap-0.5 tpl:p-1"
    >
      <template #item="{ element: blockType }">
        <div
          class="tpl:w-full"
          @mouseenter="onPaletteRowHoverEnter(blockType, $event)"
          @mouseleave="onPaletteRowHoverLeave()"
          @focusin="onPaletteRowHoverEnter(blockType, $event)"
          @focusout="onPaletteRowHoverLeave()"
        >
          <button
            type="button"
            :data-palette-type="blockType.type"
            :disabled="paletteRowBlocked(blockType)"
            :aria-label="
              paletteBlockedReason(blockType) ??
              format(t.sidebarNav.insertBlock, { block: blockType.label })
            "
            class="tpl-sidebar-palette-row-btn tpl:flex tpl:h-10 tpl:w-full tpl:items-center tpl:gap-3 tpl:rounded-[var(--tpl-radius-sm)] tpl:border-none tpl:bg-transparent tpl:px-3 tpl:text-[var(--tpl-text-muted)] tpl:transition-all tpl:duration-[120ms] tpl:ease-[cubic-bezier(0.16,1,0.3,1)] active:tpl:cursor-grabbing disabled:tpl:cursor-not-allowed disabled:tpl:opacity-45 disabled:active:tpl:cursor-not-allowed"
            :class="[
              paletteRowBlocked(blockType)
                ? 'tpl-sidebar-palette-item--blocked tpl:cursor-default'
                : 'tpl:cursor-grab hover:tpl:bg-[var(--tpl-primary-light)] hover:tpl:text-[var(--tpl-primary)]',
            ]"
            :style="{
              justifyContent: isExpanded ? 'flex-start' : 'center',
            }"
            @click="insertBlockFromItem(blockType)"
            @keydown="handlePaletteKeydown($event, blockType)"
          >
            <div
              class="tpl:flex tpl:shrink-0 tpl:items-center tpl:justify-center tpl:transition-transform tpl:duration-[120ms] tpl:ease-[cubic-bezier(0.16,1,0.3,1)]"
              :class="paletteRowBlocked(blockType) ? '' : 'hover:tpl:scale-105'"
            >
              <component
                :is="blockTypeIcons[blockType.type]"
                v-if="blockTypeIcons[blockType.type]"
                :size="20"
                :stroke-width="1.5"
              />
              <CustomBlockIcon
                v-else-if="blockType.isCustom"
                :icon="blockType.icon"
                :size="20"
              />
            </div>
            <span
              v-if="isExpanded"
              class="tpl:truncate tpl:text-sm tpl:font-medium"
            >
              {{ blockType.label }}
            </span>
          </button>
        </div>
      </template>
    </draggable>
  </aside>
  <Teleport to="body">
    <div
      v-if="paletteBlockedTooltipPos"
      role="tooltip"
      class="tpl-palette-blocked-tooltip"
      :style="{
        left: `${paletteBlockedTooltipPos.left}px`,
        top: `${paletteBlockedTooltipPos.top}px`,
      }"
    >
      {{ paletteBlockedTooltipPos.message }}
    </div>
  </Teleport>
</template>
